import { HeroUIProvider } from '@heroui/react';
import { QueryClientProvider, type QueryClient } from '@tanstack/react-query';
import { useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useHref, useNavigate } from 'react-router-dom';
import { applyDocumentDirection, currentLocale } from '../i18n/index.js';
import { ToastProvider } from '../components/ui/Toast.js';

/**
 * Application providers.
 *
 * THE LOCALE PROP IS NOT COSMETIC. HeroUI is built on react-aria, which decides
 * direction from the locale it is given — NOT from `document.dir`. Its default
 * is `en-US`. Before Phase 2 the provider was mounted with no locale, so under
 * Arabic the document was correctly `dir="rtl"` while every react-aria-driven
 * behaviour underneath still believed it was left-to-right: popovers and menus
 * resolved `bottom-end` to the wrong edge, arrow keys moved the wrong way in
 * tables and menus, and slider and date-segment interaction was mirrored
 * against the text. Passing the locale is the single fix for all of it.
 *
 * The `navigate`/`useHref` pair hands HeroUI the router, so a HeroUI `Link` or
 * a pressable row navigates client-side instead of triggering a full page load.
 */

/**
 * BCP 47 tags handed to react-aria. Region matters to its number and date
 * behaviour, and so does the numbering system.
 *
 * `-u-nu-latn` on the Arabic tag is deliberate and was found by looking at a
 * real browser: plain `ar-SA` makes react-aria's date segments render
 * Arabic-Indic digits (٠٤), because that is the default numbering system for
 * the locale. Everywhere else in this product numbers are Western digits — a
 * value on screen has to match the same value on an instrument display, on a
 * supplier's certificate and on a printed report, and a date field that
 * disagreed with `formatNumber` would be the one place the interface
 * contradicted itself. The extension pins the digits without changing anything
 * else about the locale: the calendar, month names, and first day of the week
 * all stay Arabic.
 *
 * Recorded as a Phase 2 decision in PLAN.md section 16.1c.
 */
const REGIONAL_LOCALE: Record<string, string> = {
  ar: 'ar-SA-u-nu-latn',
  en: 'en-GB',
};

function LocaleBridge({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const locale = currentLocale();

  // Keep `<html lang dir class>` in step with the active language, including
  // when it changes from somewhere other than the switcher — a saved user
  // preference arriving after sign-in, for example.
  useEffect(() => {
    applyDocumentDirection(locale);
  }, [locale, i18n.resolvedLanguage]);

  return (
    <HeroUIProvider
      locale={REGIONAL_LOCALE[locale] ?? locale}
      navigate={navigate}
      useHref={useHref}
    >
      <ToastProvider>{children}</ToastProvider>
    </HeroUIProvider>
  );
}

/**
 * Everything the application needs in context.
 *
 * Mounted INSIDE the router, because the locale bridge hands the router to
 * HeroUI. `App` therefore owns `BrowserRouter` and renders this beneath it.
 */
export function Providers({
  queryClient,
  children,
}: {
  queryClient: QueryClient;
  children: ReactNode;
}) {
  return (
    <LocaleBridge>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </LocaleBridge>
  );
}
